import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getMasterEquipmentDefinitions,
  getMasterEquipment,
  createMasterEquipment,
  updateMasterEquipment,
  deleteMasterEquipment,
  uploadMasterEquipmentImage,
  bulkUpdateMasterEquipment
} from '../services/api';
import EquipmentSelector from './EquipmentSelector';
import {
  Box,
  Flex,
  Button,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDescription,
  IconButton,
  VStack,
  useDisclosure,
  HStack,
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Switch,
  useToast,
  Input,
  Image,
  Badge,
  GridItem,
  CloseButton,
  useBreakpointValue,
  Heading,
  Spinner,
  Checkbox
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, AddIcon, DeleteIcon } from '@chakra-ui/icons';
import logo from '../assets/logo.jpg';
import * as XLSX from 'xlsx';

function MasterSelectedEquipmentModal({ item, config, isTsubo, isOpen, onClose, isNew = false }) {
    if (!isOpen) return null;
    const { isFunctionEnabled } = useAuth();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [editValues, setEditValues] = useState({});
    const navigate = useNavigate();
    const [uploading, setUploading] = useState(false);
    const [images, setImages] = useState(isNew ? [] : item.images && item.images.length > 0 ? item.images : []);
  
    // 画像URLの配列を取得
    const imageUrls = React.useMemo(() => {
      if (!item) return [];
      
      // 新形式（images配列）をチェック
      if (item.images && item.images.length > 0) {
        return item.images;
      }
      
      // 旧形式（個別のURL）をチェック
      const urls = [];
      for (let i = 1; i <= 5; i++) {
        const url = item[`image${i}_sas_url`];
        if (url) urls.push(url);
      }
      return urls;
    }, [item]);
  
    useEffect(() => {
      setImages(isNew ? [] : item.images && item.images.length > 0 ? item.images : []);
    }, [item, isNew]);
  
    useEffect(() => {
      setCurrentIndex(0);
      if (item) {
        setEditValues({ ...item });
      }
    }, [item, isNew]);
  
    const goToNext = useCallback(() => {
      if (imageUrls.length > 1) {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % imageUrls.length);
      }
    }, [imageUrls.length]);
  
    const goToPrevious = useCallback(() => {
      if (imageUrls.length > 1) {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + imageUrls.length) % imageUrls.length);
      }
    }, [imageUrls.length]);
  
    useEffect(() => {
      if (imageUrls.length > 1) {
        const timer = setTimeout(goToNext, 3000);
        return () => clearTimeout(timer);
      }
    }, [currentIndex, imageUrls.length, goToNext]);
  
    if (!item) return null;
  
    const handlePrevImageClick = (e) => { e.stopPropagation(); goToPrevious(); };
    const handleNextImageClick = (e) => { e.stopPropagation(); goToNext(); };
  
    // Chakra UIのカラーモードに基づく色の設定
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const subTextColor = useColorModeValue('gray.600', 'gray.400');
  
    const handleSave = async () => {
      try {
        // 編集可能なフィールドのみ抽出
        const editableFieldNames = config.fields
          .filter(field =>
            ![
              'id', 'user_id', 'created_at', 'updated_at', 'is_liked', 'is_fixed', 'locked_at',
              'signature_url', 'memo', 'material_files', 'preferenceId'
            ].includes(field.name)
          )
          .map(field => field.name);
  
        const payload = {};
        editableFieldNames.forEach(name => {
          if (editValues.hasOwnProperty(name)) {
            payload[name] = editValues[name];
          }
        });
        if (isNew) {
          await createMasterEquipment(
            config.key,
            payload
          );
        } else {
          await updateMasterEquipment(
            config.key,
            item.id,
            payload
          );
        }
        if (onClose) {
          onClose(); // 編集完了後にモーダルを閉じる
        }
      } catch (e) {
        alert('保存に失敗しました');
      }
    };
  
    const handleEditFloorplan = () => {
      const url = `/admin/master-floor`;
      navigate(url, { state: { floorRooms: item.rooms || [], 
        mode: isNew ? 'new_master' : 'edit_master', item_id: isNew ? null : item.id} });
    };

    const handleUploadImage = async (file) => {
      setUploading(true);
      const res = await uploadMasterEquipmentImage(file);
      setImages([...images, res.url]);
      setEditValues(v => ({ ...v, images: [...images, res.url] }));
      setUploading(false);
    };
  
    return (
      <Modal isOpen={true} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="800px" maxH="90vh" overflowY="auto">
          <ModalHeader borderBottomWidth="1px">
            {isNew ? '新規マスタ設備' : `${item.manufacturer} - ${item.name}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={4}>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
              {/* 画像セクション */}
              <Box position="relative" borderWidth="1px" borderColor={borderColor} borderRadius="md" overflow="hidden" minH="300px">
                {imageUrls.length > 0 ? (
                  <>
                    <Image
                      src={imageUrls[currentIndex]}
                      alt={`${item.name} - 画像 ${currentIndex + 1}`}
                      w="100%"
                      h="300px"
                      objectFit="contain"
                    />
                    {imageUrls.length > 1 && (
                      <>
                        <IconButton
                          icon={<ChevronLeftIcon />}
                          aria-label="前の画像"
                          position="absolute"
                          left={2}
                          top="50%"
                          transform="translateY(-50%)"
                          onClick={handlePrevImageClick}
                          colorScheme="blackAlpha"
                        />
                        <IconButton
                          icon={<ChevronRightIcon />}
                          aria-label="次の画像"
                          position="absolute"
                          right={2}
                          top="50%"
                          transform="translateY(-50%)"
                          onClick={handleNextImageClick}
                          colorScheme="blackAlpha"
                        />
                      </>
                    )}
                  </>
                ) : (
                  <Flex
                    w="100%"
                    h="300px"
                    bg="gray.100"
                    align="center"
                    justify="center"
                    color="gray.500"
                  >
                    画像なし
                  </Flex>
                )}
              </Box>
  
              {/* 詳細情報セクション */}
              <VStack align="stretch" spacing={4}>
                  <>
                    {config.fields?.map(field => {
                      if ([
                        'id', 'user_id', 'created_at', 'updated_at', 'images', 'signature_url', 'locked_at', 'preferenceId', 'is_liked', 'is_fixed', 'memo', 'material_files'
                      ].includes(field.name)) return null;
                      // boolean型
                      if (field.type === 'boolean') {
                        return (
                          <Box key={field.name}>
                            <Text fontSize="sm" color={subTextColor}>{field.label}</Text>
                            <input
                              type="checkbox"
                              checked={!!editValues[field.name]}
                              onChange={e => setEditValues(v => ({ ...v, [field.name]: e.target.checked }))}
                            />
                          </Box>
                        );
                      }
                      // select型
                      if (field.type === 'select') {
                        return (
                          <Box key={field.name}>
                            <Text fontSize="sm" color={subTextColor}>{field.label}</Text>
                            <select
                              value={editValues[field.name] || ''}
                              onChange={e => setEditValues(v => ({ ...v, [field.name]: e.target.value }))}
                            >
                              <option value="">選択してください</option>
                              {field.options?.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </Box>
                        );
                      }
                      // number/string型
                      return (
                        <Box key={field.name}>
                          <Text fontSize="sm" color={subTextColor} mb={1}>{field.label}</Text>
                          <Input
                            type={field.type === 'number' ? 'number' : 'text'}
                            value={editValues[field.name] ?? ''}
                            onChange={e => setEditValues(v => ({ ...v, [field.name]: field.type === 'number' ? Number(e.target.value) : e.target.value }))}
                            size="sm" // サイズを調整
                          />
  
                        </Box>
                      );
                  
                    })}
                    <Box mt={2}>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          await handleUploadImage(file);
                        }}
                        disabled={uploading}
                      />
                      {uploading && <Text fontSize="xs" color="gray.500">アップロード中...</Text>}
                      {images && (
                        <Box mt={1}>
                          {images.map((url, index) => (
                            <Box key={url} position="relative" display="inline-block" mr={2} mb={2}>
                              <img src={url} alt={`設備画像 ${index + 1}`} style={{ maxWidth: 120, maxHeight: 120 }} />
                              <CloseButton
                                size="sm"
                                position="absolute"
                                top="2px"
                                right="2px"
                                color="red.500"
                                bg="whiteAlpha.800"
                                borderRadius="full"
                                onClick={() => {
                                  setImages(images.filter((_, i) => i !== index))
                                  setEditValues(v => ({ ...v, images: images.filter((_, i) => i !== index) }))
                                }}
                                zIndex={1}
                              />
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                    <Box>
                        {config.key === 'floorplan' && isFunctionEnabled('floor_plan') && (
                            <Button bg="black" color="white" onClick={handleEditFloorplan}>間取りを編集</Button>
                        )}
                    </Box>
                    <HStack>
                      <Button bg="black" color="white" onClick={handleSave}>保存</Button>
                      <Button onClick={() => onClose()}>キャンセル</Button>
                    </HStack>
                  </>
                </VStack>
            </Grid>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  } 

function MasterEquipmentList({
  items,
  config,
  isTsubo = false,
  onReloadItems
}) {
  const { isOpen: isMasterModalOpen, onOpen: onMasterModalOpen, onClose: onMasterModalClose } = useDisclosure();
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subTextColor = useColorModeValue('gray.600', 'gray.400');
  const tileBg = useColorModeValue('white', 'gray.800');
  const tileBorder = useColorModeValue('gray.200', 'gray.600');
  const [selectedItems, setSelectedItems] = useState([]);

  const [selectedItemId, setSelectedItemId] = useState(null);

  const [isNew, setIsNew] = useState(false);
  const [uploadingMulti, setUploadingMulti] = useState(false);

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '価格未定';
    return `¥${price.toLocaleString()}`;
  };

  const handleMultiSelectChange = (itemId, e) => {
    if (e.target.checked) {
      setSelectedItems(prev => [...prev, itemId]);
    } else {
      setSelectedItems(prev => prev.filter(id => id !== itemId));
    } 
  };

  const handleUploadImageMulti = async (file, selectedItems) => {
    setUploadingMulti(true);
    const res = await uploadMasterEquipmentImage(file);
    const newImageUrl = res.url;
    selectedItems.map(async (itemId) => {
      const item = items.find(item => item.id === itemId);
      if (item) {
        if(item.images) {
          item.images.push(newImageUrl);
        } else {
          item.images = [newImageUrl];
        }
        const res = await updateMasterEquipment(config.key, item.id, { images: item.images });
        items.map(item => item.id === itemId ? res : item);
      }
    });
    toast({
      title: "画像をアップロードしました。続けてアップロードする場合は、チェックボックス見直しの上、ファイルを再選択してください。",
      status: "success",
      duration: 10000,
      isClosable: true,
    });
    setUploadingMulti(false);
  };

  // 画像URLの取得
  const getImageUrl = (item) => {
    // 新しい形式（images配列）をチェック
    if (item.images && item.images.length > 0) {
      return item.images[0];
    }
    // 古い形式（個別のURL）をチェック
    if (item.image1_sas_url) {
      return item.image1_sas_url;
    }
    return null;
  };

  const selectedItem = useMemo(() => {
    if (selectedItemId === null){
      const item = {
        item_type: config.key,
      };
      return item;
    }
    return items.find(item => item.id === selectedItemId);
  }, [selectedItemId, items]);

  const onItemSelect = (itemId) => {
    setIsNew(false);
    setSelectedItemId(itemId);
    onMasterModalOpen();
  };
  const onNewItemSelect = () => {
    setSelectedItemId(null);
    setIsNew(true);
    onMasterModalOpen();
  };
  const onCloseMasterModal = () => {
    onMasterModalClose();
    getMasterEquipment(config.key);
    onReloadItems();
  };

  return (
    <Box position="relative">
    {selectedItems.length > 0 && (
    <Box position="fixed" width="100%" bottom="20px" bg="gray.100" p={4} borderRadius="md" borderWidth="1px" borderColor="gray.300" zIndex={1000}>
      <VStack>
        <Heading size="sm">まとめて画像アップロード</Heading>
        <Input
          type="file"
          accept="image/*"
          onChange={async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            await handleUploadImageMulti(file, selectedItems);
          }}
          disabled={uploadingMulti}
        />
        {uploadingMulti && <Text fontSize="xs" color="gray.500">アップロード中...</Text>}
      </VStack>
    </Box>
    )}
    <Grid
      templateColumns={{
        base: 'repeat(1, 1fr)',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(3, 1fr)',
        lg: 'repeat(4, 1fr)'
      }}
      gap={4}
    >
        <GridItem>
        <Box
          borderWidth="1px"
          borderColor={tileBorder}
          borderRadius="md"
          p={4}
          bg={tileBg}
          boxShadow="sm"
          transition="background 0.2s, box-shadow 0.2s"
          cursor="pointer" // _hover={{ bg: useColorModeValue('blue.50', 'blue.900'), boxShadow: 'md' }}
          _hover={{ bg: useColorModeValue('gray.100', 'gray.700'), boxShadow: 'md' }}
          onClick={onNewItemSelect}
          display="flex"
          alignItems="center"
          justifyContent="center"
          h="100%"
        >
          <VStack spacing={2}>
            <Box
              w="70px"
              h="70px"
              borderRadius="md"
              bg={useColorModeValue('gray.200', 'gray.600')}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <AddIcon boxSize="24px" color={useColorModeValue('black', 'white')} />
            </Box>
            <Text color={useColorModeValue('black', 'white')} fontWeight="bold">
              マスタ{config.key==='floorplan' ? '間取り' : '設備'}を登録
            </Text>
          </VStack>
        </Box>
        </GridItem>

      {items.map(item => {
        return (
        <GridItem key={item.id}>
          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={cardBorder}
            borderRadius="lg"
            overflow="hidden"
            position="relative"
            transition="transform 0.2s"
            _hover={{ transform: 'translateY(-2px)' }}
          >
              <Checkbox
                size="lg"
                position="relative"
                colorScheme="gray"
                bg="gray.100"
                onChange={(e) => handleMultiSelectChange(item.id, e)}
                zIndex={100}
                borderColor="gray.300"
                borderWidth="1px"
              />

            <Box position="relative">
              {getImageUrl(item) ? (
                <Image
                  src={getImageUrl(item)}
                  alt={item.name}
                  w="100%"
                  h="200px"
                  objectFit="contain"
                  onClick={() => onItemSelect(item.id)}
                />
              ) : (
                <Flex
                  w="100%"
                  h="200px"
                  bg="gray.100"
                  align="center"
                  justify="center"
                  color="gray.500"
                  onClick={() => onItemSelect(item.id)}
                  cursor="pointer"
                >
                  画像なし
                </Flex>
              )}
              <HStack
                position="absolute"
                top={2}
                right={2}
                spacing={1}
              >
                    <IconButton
                      size="sm"
                      icon={<DeleteIcon />}
                      colorScheme="yellow"
                      aria-label="削除"
                      onClick={async () => {
                        if (!window.confirm('本当にこの設備を削除してもよいですか？')) return;
                        try {
                          await deleteMasterEquipment(config.key, item.id);
                          toast({
                            title: "アイテムを削除しました",
                            status: "success",
                            duration: 3000,
                            isClosable: true,
                          });
                          onReloadItems();
                        }
                        catch (error) {
                          toast({
                            title: "エラー",
                            description: error.message,
                            status: "error",
                          });
                        }
                      }}
                      isRound
                    />
              </HStack>
            </Box>


            <VStack p={4} align="stretch" spacing={2}>
              <Flex justify="space-between" align="center">
                <Text fontSize="sm" color={subTextColor}>
                  {item.manufacturer}
                </Text>
                 {item.recommended && (
                  <Badge colorScheme="black">おすすめ</Badge>
                )}
              </Flex>
              <Text
                fontSize="md"
                fontWeight="bold"
                color={textColor}
                noOfLines={2}
                onClick={() => onItemSelect(item.id)}
                cursor="pointer"
              >
                {item.name}
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="black">
                {formatPrice(item.price)}
              </Text>
              <Text fontSize="sm" color={subTextColor} noOfLines={2}>
                {item.features}
              </Text>

              {config.fields?.map(field => {
                const value = item[field.name];
                if (value === undefined || value === null || 
                    field.name === 'price' || field.name === 'features' || 
                    field.name === 'recommended' || field.name === 'manufacturer' || 
                    field.name === 'name' || field.name === 'images' ||
                    (item.is_user_equipment && field.name === 'user_id')) return null;
                // floorplanのm2のみ単位切替
                if (config.key === 'floorplan' && field.unit === 'm2') {
                  return (
                    <Text key={field.name} fontSize="sm" color={subTextColor}>
                      {field.label}: {isTsubo ? (value ? (Number(value) / 3.305785).toFixed(2) + '坪' : '') : value + 'm²'}
                    </Text>
                  );
                }
                return (
                  <Text key={field.name} fontSize="sm" color={subTextColor}>
                    {field.label}: {typeof value === 'boolean' ? (value ? 'あり' : 'なし') : value}{field.unit || ''}
                  </Text>
                );
              })}
            </VStack>
          </Box>
        </GridItem>
        );
      })}
    </Grid>
    <MasterSelectedEquipmentModal
      item={selectedItem}
      config={config}
      isTsubo={isTsubo}
      isOpen={isMasterModalOpen}
      onClose={onCloseMasterModal}
      isNew={isNew}
    />
    </Box>
  );
} 

export default function MasterEquipment({}) {
  const { currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [equipmentDefinitions, setEquipmentDefinitions] = useState({});
  const [definitionsLoading, setDefinitionsLoading] = useState(true);

  // --- State ---
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState('floorplan');
  const [fixedMap, setFixedMap] = useState({});
  const isWide = useBreakpointValue({ base: false, md: true });
  const sidebarDisclosure = useDisclosure({ defaultIsOpen: isWide });
  const [isTsubo, setIsTsubo] = useState(false); // 単位切替（坪）
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef();
  const [isUploading, setIsUploading] = useState(false);

  // 設備定義を取得
  const loadEquipmentDefinitions = useCallback(async () => {
    if (!currentUser) {
      setDefinitionsLoading(false);
      setError("ユーザー情報がありません。");
      return;
    }
    setDefinitionsLoading(true);
    setError(null);
    try {
      const definitions = await getMasterEquipmentDefinitions();
      if (!definitions || Object.keys(definitions).length === 0) {
        throw new Error('設備定義が空です');
      }
      setEquipmentDefinitions(definitions);
    } catch (err) {
      console.error("設備定義の取得に失敗しました:", err);
      setError(err.message);
      setEquipmentDefinitions({});
    } finally {
      setDefinitionsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadEquipmentDefinitions();
  }, [loadEquipmentDefinitions]);


  // 現在選択中の設備の設定を取得
  const currentEquipmentConfig = useMemo(() => {
    if (definitionsLoading || Object.keys(equipmentDefinitions).length === 0) {
      let label = '設備定義を読込中...';

      if (!definitionsLoading && Object.keys(equipmentDefinitions).length === 0) {
        label = error ? '設備定義読込エラー' : '設備定義なし';
      }
      return {
        key: selectedEquipment,
        label: label,
        enabled: false,
        icon: '❓',
        fields: []
      };
    }

    const definition = equipmentDefinitions['equipment_types'][selectedEquipment];
    if (!definition) {
      console.warn(`設備定義が見つかりません: ${selectedEquipment}`);
      return {
        key: selectedEquipment,
        label: '定義が見つかりません',
        enabled: false,
        icon: '❓',
        fields: []
      };
    }
    return {
      key: selectedEquipment,
      label: definition.name,
      enabled: definition.enabled,
      icon: definition.icon,
      fields: Object.entries(definition.fields || {}).map(([name, field]) => ({
        name,
        ...field
      }))
    };
  }, [selectedEquipment, equipmentDefinitions, definitionsLoading, error]);


// 設備アイテムとユーザー設定の読み込み (selectedEquipment や targetUserId が変わった時に実行)
const loadItems = useCallback(async () => {
  if (!currentUser || definitionsLoading || !equipmentDefinitions.equipment_types || !equipmentDefinitions.equipment_types[selectedEquipment]) {
    if (!definitionsLoading && equipmentDefinitions.equipment_types && !equipmentDefinitions.equipment_types[selectedEquipment]) {
      console.warn(`選択された設備 (${selectedEquipment}) の定義が equipmentDefinitions.equipment_types に見つかりません。`);
    }
    setLoading(false);
    return;
  }
  setLoading(true);
  setError(null);
  try {
    const itemsData = await getMasterEquipment(selectedEquipment);
    setItems(itemsData);
  } catch (err) {
    console.error("アイテム・設定データの取得に失敗しました:", err);
    setError(err.message);
    setItems([]);
  } finally {
    setLoading(false);
  }
}, [currentUser, selectedEquipment, equipmentDefinitions, definitionsLoading]);

// selectedEquipment, targetUserId, または definitionsLoading の完了後にアイテムと設定を読み込む
useEffect(() => {
  if (!definitionsLoading) { // definitionsLoading が false になってから実行
    loadItems();
  }
}, [loadItems, definitionsLoading]); // definitionsLoading も依存配列に追加


  // --- ここで isFloorplanSelected を定義 ---
  const isFloorplanSelected = useMemo(() => {
    return selectedEquipment === 'floorplan';
  }, [selectedEquipment]);

  // 選択されたアイテム
  const selectedItem = useMemo(() => {
    if (selectedItemId === null) return null;
    return items.find(item => item.id === selectedItemId);
  }, [selectedItemId, items]);

  // ハンドラー
  const handleItemSelect = (itemId) => { setSelectedItemId(itemId); };
  const closeModal = () => { setSelectedItemId(null); };

  // 画面幅が変わったら開閉を自動で切り替える
  useEffect(() => {
    if (isWide && !sidebarDisclosure.isOpen) {
      sidebarDisclosure.onOpen();
    } else if (!isWide && sidebarDisclosure.isOpen) {
      sidebarDisclosure.onClose();
    }
    // eslint-disable-next-line
  }, [isWide]);

  // Excelエクスポート
  const handleExportExcel = () => {
    if (!items || items.length === 0) {
      toast({ title: 'データがありません', status: 'warning' });
      return;
    }
    const unUseFields = ['images', 'rooms', 'created_at', 'updated_at',
        '_rid', '_self', '_etag', '_attachments', '_ts', 'type', 'tenant_id'
    ];
    
    // fieldsで定義されたすべてのフィールド名を取得（unUseFieldsを除く）
    const definedFields = ['id']; // idは常に先頭に配置
    if (currentEquipmentConfig && currentEquipmentConfig.fields) {
      currentEquipmentConfig.fields.forEach(field => {
        if (!unUseFields.includes(field.name) && field.name !== 'id') {
          definedFields.push(field.name);
        }
      });
    }
    
    const data = items.map(item => {
      // 定義されたフィールドすべてを含むオブジェクトを作成
      const obj = {};
      definedFields.forEach(fieldName => {
        obj[fieldName] = item[fieldName] || ''; // 存在しないフィールドは空文字
      });
      
      // 定義されていないが実際に存在するフィールドも追加（unUseFieldsを除く）
      Object.keys(item).forEach(key => {
        if (!unUseFields.includes(key) && !definedFields.includes(key)) {
          obj[key] = item[key];
        }
      });
      
      return obj;
    });
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MasterEquipment');
    XLSX.writeFile(wb, `設備マスタ_${selectedEquipment}.xlsx`);
  };

  // Excelインポート
  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    try {
      await bulkUpdateMasterEquipment(selectedEquipment, file);
      toast({
        title: `アップロード完了しました`,
        duration: 8000,
        isClosable: true,
      });
    } catch (err) {
        toast({
            title: `アップロード失敗しました。エラー確認し、再度Excelのダウンロード、編集、アップロードをお願いします。`,
            description: err.message,
            status: 'error',
            duration: 30000,
            isClosable: true,
          });
    } finally {
        loadItems();
        setIsUploading(false);
    }
  };

  if (loading || definitionsLoading) {
    return (
      <Flex align="center" justify="center" minH="60vh">
        <Text fontSize="xl" color="gray.500">データを読み込み中...</Text>
      </Flex>
    );
  }

  // --- Chakra UI用の色 ---
  const headerBg = useColorModeValue('gray.50', 'gray.800');
  const headerBorder = useColorModeValue('gray.200', 'gray.700');
  const mainBg = useColorModeValue('white', 'gray.900');

  return (
    <Flex minH="100vh" w="100vw" bg={mainBg}>
      {/* サイドバー */}
      <Box
        w={sidebarDisclosure.isOpen ? '220px' : '60px'}
        minW="60px"
        bg="gray.50"
        borderRightWidth="1px" p={2}> 
        <IconButton
          icon={sidebarDisclosure.isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          onClick={sidebarDisclosure.onToggle}
          aria-label="ナビを開閉"
          mb={2}
          size="sm"
          colorScheme="gray"
          isRound
        />
        <EquipmentSelector
          onSelect={setSelectedEquipment}
          selected={selectedEquipment}
          fixedMap={fixedMap}
          equipmentList={equipmentDefinitions}
          isOpen={sidebarDisclosure.isOpen}
        />
      </Box>
      {/* メインコンテンツ */}
      <Box flex={1} px={{ base: 1, md: 4 }} minW={0}>
        <Box as="header" bg={headerBg} borderBottomWidth="1px" borderColor={headerBorder} px={4} py={3} mb={2} w="100%">
          <Flex align="center" justify="space-between">
            <Box m='30px'>
              <img src={logo} alt="logo" width="300px" height="90px" />
            </Box>
            <Button bg="black" color="white" onClick={() => navigate('/admin/users')}>お客様一覧</Button>
          </Flex>

          {/* 単位切替スイッチ（floorplanのみ） */}
          {selectedEquipment === 'floorplan' && (
            <Flex align="center" mt={2} mb={-2} gap={4}>
              <Text fontSize="sm">面積単位：m2</Text>
              <Switch isChecked={isTsubo} onChange={e => setIsTsubo(e.target.checked)} colorScheme="teal" />
              <Text fontSize="sm">坪</Text>
            </Flex>
          )}
        </Box>

        <Flex gap={4} position="relative" flexDirection="column">
          {/* 左側のメインコンテンツ */}
          <Box flex={1} bg="gray.100" minW="0" transition="width 0.3s ease-in-out" w="100%" border="1px solid gray" p={4}>
          {/* Excelエクスポート・インポートUI */}
          <Flex gap={2} mt={2} flexDirection="column">
            <Button width="150px" size="sm" bg="black" color="white" onClick={handleExportExcel}>Excel出力</Button>
            <Heading fontSize="sm">Excelアップロード</Heading>
            <Input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              onChange={handleImportExcel}
              isDisabled={isUploading}
            />
            {isUploading && <Spinner size="sm" />}
            <Heading fontSize="sm">Excelアップロードの際はこちらご注意ください</Heading>
            <Text fontSize="xs" color="black">
              ※新規作成は行を追加して入力ください。id列には、"new" を入力してください。
            </Text>
            <Text fontSize="xs" color="black">
              ※設備の削除はできません。画面上で削除ください。
            </Text>
            <Text fontSize="xs" color="black">
              ※更新したい設備のid列は編集しないでください。
            </Text>
          </Flex>
          </Box>
          <Box 
            flex={1} 
            minW="0" 
            transition="width 0.3s ease-in-out"
            w="100%"
          >
            {error && (
              <Alert status="error" mb={4} borderRadius="md">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Flex direction="column" gap={4} w="100%">
              <Box w="100%">
                <MasterEquipmentList
                  items={items}
                  config={currentEquipmentConfig}
                  isTsubo={isTsubo}
                  onReloadItems={loadItems}
                />
              </Box>
            </Flex>
          </Box>
        </Flex>

        {selectedItem && (
            <Box>
                <Text>選択されたアイテム</Text>
            </Box>
        )}
      </Box>
    </Flex>
  );
} 